// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { NoteAddOutlined } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { deleteNote } from '@/note/api/delete-note';
import { Note } from '@/note/api/types';
import { updateNote } from '@/note/api/update-note';
import { NoteAuditDrawer } from '@/note/components/note-audit-drawer';
import { NoteCard } from '@/note/components/note-card';
import { NoteDialog } from '@/note/components/note-dialog';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  /**
   * A collection of notes to display
   */
  notes: Note[];

  /**
   * Callback when notes are updated
   */
  onNotesUpdated: () => void;
}

/**
 * A list that displays a collection of notes with options to edit, delete, or view audit logs.
 * This component is entity-agnostic and relies on callbacks for data refreshing.
 * The design follows a modern chat-like interface with clear attribution and easy access to actions.
 */
export function NoteList(props: Props) {
  const { notes, onNotesUpdated } = props;

  // Sort notes by last updated date (descending)
  const sortedNotes = [...notes].sort(
    (a, b) => b.lastUpdatedAt.toMillis() - a.lastUpdatedAt.toMillis()
  );

  // Dialog states
  const editDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const auditDrawer = useDisclosure();
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);

  // Update note mutation
  const updateNoteMutation = useMutation({
    mutationFn: (content: string) => {
      if (!selectedNote) throw new Error('No note selected');
      return updateNote(selectedNote.key, { content });
    },
    onSuccess: () => {
      // Call the parent's onNotesUpdated callback to handle refreshing
      onNotesUpdated();

      // Close the dialog
      editDialog.close();
    },
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: () => {
      if (!selectedNote) throw new Error('No note selected');
      return deleteNote(selectedNote.key);
    },
    onSuccess: () => {
      // Call the parent's onNotesUpdated callback to handle refreshing
      onNotesUpdated();

      // Close the dialog
      deleteDialog.close();
    },
  });

  // Handlers for note actions
  const handleEditNote = (note: Note) => {
    setSelectedNote(note);
    editDialog.open();
  };

  const handleDeleteNote = (note: Note) => {
    setSelectedNote(note);
    deleteDialog.open();
  };

  const handleViewAudit = (note: Note) => {
    setSelectedNote(note);
    auditDrawer.open();
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '200px' }} paddingTop={2}>
      {/* Edit Note Dialog */}
      {selectedNote && (
        <NoteDialog
          isOpen={editDialog.isOpen}
          onClose={editDialog.close}
          onSubmit={(content) => updateNoteMutation.mutate(content)}
          isSubmitting={updateNoteMutation.isPending}
          title="Edit note"
          initialContent={selectedNote.content}
          submitButtonText="Save"
        />
      )}

      {/* Delete Note Dialog */}
      {selectedNote && (
        <Dialog open={deleteDialog.isOpen} onClose={deleteDialog.close}>
          <DialogTitle>Delete note</DialogTitle>
          <DialogContent dividers>
            <Typography marginBottom={1}>
              Are you sure you want to delete this note? The note will be
              permanently removed.
            </Typography>
            <Typography>
              (Note: your audit log will continue to show a record of the
              deleted note.)
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={deleteDialog.close}
              disabled={deleteNoteMutation.isPending}
            >
              No, cancel
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={() => deleteNoteMutation.mutate()}
              disabled={deleteNoteMutation.isPending}
            >
              Yes, delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Audit Log Drawer */}
      {selectedNote && (
        <NoteAuditDrawer noteKey={selectedNote.key} disclosure={auditDrawer} />
      )}

      {/* Note Cards or Empty State */}
      {sortedNotes.length === 0 ? (
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4,
            textAlign: 'center',
            minHeight: '200px',
          }}
        >
          <NoteAddOutlined
            sx={{ fontSize: 48, color: 'text.secondary', marginBottom: 2 }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No notes yet
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: '400px', marginBottom: 3 }}
          >
            Notes help you track information about this project
          </Typography>
        </Card>
      ) : (
        <Stack spacing={2}>
          {sortedNotes.map((note) => (
            <NoteCard
              key={note.key}
              note={note}
              onEdit={() => handleEditNote(note)}
              onDelete={() => handleDeleteNote(note)}
              onViewAudit={() => handleViewAudit(note)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
